/**
 * Utility functions for wallet-draining wheel sharing functionality
 * These utilities help dominants share wheels with their paypigs and cash slaves
 * Send your financial domination wheels and watch the money flow in
 */

// Base URL for the application
const BASE_URL = window.location.origin;

/**
 * Generate a share link to send to your paypig
 * @param shareId The unique ID of the share
 * @param accessCode Optional access code for the share (for extra control over your cash slave)
 * @returns The full share URL to send to your wallet-draining victim
 */
export const generateShareLink = (shareId: string, accessCode?: string): string => {
  const baseShareUrl = `${BASE_URL}/shared/${shareId}`;

  // If access code is provided, append it to the URL
  if (accessCode) {
    return `${baseShareUrl}/${accessCode}`;
  }

  return baseShareUrl;
};

/**
 * Generate a share link for shared ATMs
 * @param shareId The unique ID of the ATM share
 * @param accessCode Optional access code for the share
 * @returns The full share URL for the ATM
 */
export const generateAtmShareLink = (shareId: string, accessCode?: string): string => {
  const baseShareUrl = `${BASE_URL}/shared-atm/${shareId}`;

  // If access code is provided, append it to the URL
  if (accessCode) {
    return `${baseShareUrl}/${accessCode}`;
  }

  return baseShareUrl;
};

/**
 * Check if a share sent to a cash slave is still valid
 * @param sharedWheel The shared wheel object
 * @returns True if the share is valid and the paypig can still spin and drain their wallet, false otherwise
 */
export const isShareValid = (sharedWheel: any): boolean => {
  if (!sharedWheel || !sharedWheel.settings) {
    return false;
  }

  // Check if the share is active
  if (!sharedWheel.settings.isActive) {
    return false;
  }

  // Check if the share has expired
  if (sharedWheel.settings.expiresAt) {
    // Check if it's a serverTimestamp object (has _methodName property)
    const expiresAt = sharedWheel.settings.expiresAt as any;
    if (expiresAt && typeof expiresAt === 'object' && expiresAt._methodName === 'serverTimestamp') {
      // If it's a serverTimestamp that hasn't been resolved yet, consider it valid
      return true;
    }

    const expiryDate =
      sharedWheel.settings.expiresAt instanceof Date
        ? sharedWheel.settings.expiresAt
        : typeof sharedWheel.settings.expiresAt.toDate === 'function'
          ? sharedWheel.settings.expiresAt.toDate()
          : null;

    if (expiryDate && expiryDate < new Date()) {
      return false;
    }
  }

  // Check if the share has reached its maximum number of spins
  if (
    sharedWheel.settings.maxSpins !== null &&
    sharedWheel.currentSpinCount >= sharedWheel.settings.maxSpins
  ) {
    return false;
  }

  return true;
};

/**
 * Format a share expiration date for display
 * @param expiresAt The expiration date when your paypig's wallet-draining access expires
 * @returns Formatted date string showing when the financial domination ends
 */
export const formatExpirationDate = (expiresAt: Date | any): string => {
  if (!expiresAt) {
    return 'Never';
  }

  // Check if it's a serverTimestamp object (has _methodName property)
  if (expiresAt && typeof expiresAt === 'object' && expiresAt._methodName === 'serverTimestamp') {
    return 'Processing...';
  }

  // Handle Date objects
  if (expiresAt instanceof Date) {
    return expiresAt.toLocaleDateString() + ' ' + expiresAt.toLocaleTimeString();
  }

  // Handle Firestore Timestamp objects (has toDate method)
  if (typeof expiresAt === 'object' && typeof expiresAt.toDate === 'function') {
    const date = expiresAt.toDate();
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  }

  // Check if it's a string (from localStorage)
  if (typeof expiresAt === 'string') {
    try {
      const date = new Date(expiresAt);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
      }
    } catch (e) {
      console.error('Error parsing date string:', e);
    }
  }

  return 'Invalid date';
};
